/**
 * F09 v2 — admin verification label + tone helpers.
 *
 * Shared vocabularies (entity types, kinds, document kinds, rejection /
 * revocation categories, candidate / watchlist reasons, risk flags,
 * prerequisites) read the shared `verification:*` namespace. Admin-only
 * vocabularies (8 request statuses, strengths, ID-document types, event kinds)
 * read the admin overlay `admin:verification.*`.
 */
import type { useI18n } from '@bravemove/i18n/react';
import type { BadgeTone } from '@bravemove/ui-web';
import {
  requiredDocumentGroups,
  strengthOf,
  type AdminVerificationQueueItem,
  type AdminVerificationRequestDetail,
  type CandidateReasonCode,
  type IdDocumentType,
  type VerifiableEntityType,
  type VerificationDocumentKind,
  type VerificationEventKind,
  type VerificationPrerequisiteCode,
  type VerificationReasonCategory,
  type VerificationRequestKind,
  type VerificationRequestStatus,
  type VerificationRevocationCategory,
  type VerificationRiskFlag,
  type VerificationStrength,
  type WatchlistReasonCode,
} from '@bravemove/types';

export type Translate = ReturnType<typeof useI18n>['t'];

export function entityLabel(t: Translate, type: VerifiableEntityType): string {
  return t(`verification:entity.${type}`);
}

export function kindLabel(t: Translate, kind: VerificationRequestKind): string {
  return t(`verification:kind.${kind}`);
}

export function requestStatusLabel(t: Translate, status: VerificationRequestStatus): string {
  return t(`admin:verification.requestStatus.${status}`);
}

export function strengthLabel(t: Translate, strength: VerificationStrength): string {
  return t(`admin:verification.strength.${strength}`);
}

export function docKindLabel(t: Translate, kind: VerificationDocumentKind): string {
  return t(`verification:docKind.${kind}`);
}

export function rejectionLabel(t: Translate, category: VerificationReasonCategory): string {
  return t(`verification:rejection.category.${category}`);
}

export function revocationLabel(t: Translate, category: VerificationRevocationCategory): string {
  return t(`verification:revocation.category.${category}`);
}

export function candidateReasonLabel(t: Translate, code: CandidateReasonCode): string {
  return t(`verification:candidateReason.${code}`);
}

/** `legacy_rereview` renders as "Grandfathered — re-review" in the admin app. */
export function watchlistReasonLabel(t: Translate, code: WatchlistReasonCode): string {
  if (code === 'legacy_rereview') return t('admin:verification.watchlist.legacyRereview');
  return t(`verification:watchlistReason.${code}`);
}

export function riskFlagLabel(t: Translate, flag: VerificationRiskFlag): string {
  return t(`verification:riskFlag.${flag}`);
}

export function prerequisiteLabel(t: Translate, code: VerificationPrerequisiteCode): string {
  return t(`verification:prerequisite.${code}.unmet`);
}

export function idTypeLabel(t: Translate, type: IdDocumentType): string {
  return t(`admin:verification.idType.${type}`);
}

export function eventLabel(t: Translate, kind: VerificationEventKind): string {
  return t(`admin:verification.event.${kind}`, { defaultValue: kind });
}

/** Human label for an evaluator signal key (falls back to the raw key). */
export function signalLabel(t: Translate, key: string): string {
  return t(`admin:verification.signals.${key}`, { defaultValue: key });
}

/** Render a signal value (booleans → Yes / No, objects → `k: v` pairs). */
export function signalValue(t: Translate, value: unknown): string {
  if (value === null || value === undefined) return t('admin:verification.common.none');
  if (typeof value === 'boolean') {
    return value ? t('admin:verification.signals.yes') : t('admin:verification.signals.no');
  }
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${String(v)}`)
      .join(', ');
  }
  return String(value);
}

/** "Signals" column summary: `Label value · Label value`. */
export function signalSummary(
  t: Translate,
  highlights: AdminVerificationQueueItem['signalHighlights'],
): string {
  if (highlights.length === 0) return t('admin:verification.queue.noSignals');
  return highlights.map((h) => `${signalLabel(t, h.key)} ${signalValue(t, h.value)}`).join(' · ');
}

/** E8 — the strength the UI shows is always `strengthOf(score, riskFlags)`. */
export function strengthFor(
  score: number | null | undefined,
  riskFlags: readonly string[],
): VerificationStrength {
  return strengthOf(score, riskFlags);
}

const STATUS_TONE: Record<VerificationRequestStatus, BadgeTone> = {
  draft: 'neutral',
  pending: 'info',
  more_info_needed: 'warning',
  approved: 'verified',
  rejected: 'danger',
  withdrawn: 'neutral',
  revoked: 'danger',
  superseded: 'neutral',
};

export function requestStatusTone(status: VerificationRequestStatus): BadgeTone {
  return STATUS_TONE[status];
}

const STRENGTH_TONE: Record<VerificationStrength, BadgeTone> = {
  strong: 'success',
  moderate: 'info',
  weak: 'warning',
};

export function strengthTone(strength: VerificationStrength): BadgeTone {
  return STRENGTH_TONE[strength];
}

const DAY_MS = 86_400_000;

/** Whole days between `iso` and `now` (never negative). */
export function ageInDays(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.floor((now - at) / DAY_MS));
}

export function ageTone(days: number): BadgeTone {
  if (days >= 7) return 'danger';
  if (days >= 3) return 'warning';
  return 'neutral';
}

/** A link styled as a small secondary action (no `<button>` nested in `<a>`). */
export const VERIFICATION_LINK_CLASS =
  'inline-flex items-center rounded-md border border-hairline px-2.5 py-1 text-[12px] font-medium text-ink hover:bg-surface-2';

/** Admin SPA path of an entity page. */
export function entityPath(type: VerifiableEntityType, id: string): string {
  return `/verifications/entities/${type}/${id}`;
}

/** Admin SPA path of a request page. */
export function requestPath(id: string): string {
  return `/verifications/requests/${id}`;
}

/** Admin SPA path of the direct-verify page, optionally pre-filled. */
export function directVerifyPath(type?: VerifiableEntityType, id?: string): string {
  if (!type || !id) return '/verifications/direct';
  const q = new URLSearchParams({ entityType: type, entityId: id });
  return `/verifications/direct?${q.toString()}`;
}

/** E23 — does approving this request require the ID fields? */
export function approveNeedsIdFields(detail: AdminVerificationRequestDetail): boolean {
  if (detail.requestKind !== 'initial') return false;
  return requiredDocumentGroups(detail.entityType, 'initial').some((g) =>
    g.anyOf.includes('government_id'),
  );
}
